import { ColDef } from '@ag-grid-enterprise/all-modules'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'

export const laboratoryGridPortionsColumnDefs: (ColDef | ExportedColDef | ExportedColGroupDef)[] = [
    { type: AgGridColumnType.CUSTOM_CHECKBOX },
    {
        headerName: i18n.tc('laboratory.grid.columns.caseNumber'),
        field: 'sample.caseStudy.casCasenumber',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        hide: true,
        width: 110
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.sampleNumber'),
        field: 'sample.samLabel',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        hide: true,
        width: 130
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.portionNumber'),
        field: 'porLabel',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 135
    },
    // TODO: Uncomment when Front Development
    /*
    {
        headerName: i18n.tc('archive.grid.archiveLocation'),
        field: 'archivingLocation',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 135
    },
    */
    {
        headerName: i18n.tc('laboratory.grid.columns.orientation'),
        field: 'fragmentOrientation.forDescription',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 110,
        cellRenderer: ({ value }: ICellRendererParams) =>
            value ? `<i class="main ${value.fragmentOrientation.forIconName}"></i>${value.fragmentOrientation.forDescription}` : ''
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.fragmentsNumber'),
        field: 'porFragments',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.INTEGER,
        width: 110
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.processor'),
        colId: 'tissueProcessor.code',
        field: 'tissueProcessor.device',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 110
    },
    {
        headerName: i18n.tc('laboratory.grid.processingStartDate'),
        field: 'porProcessingStartDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110
    },
    {
        headerName: i18n.tc('laboratory.grid.processingStartPersonnel'),
        colId: 'processingStartPersonnel.normalizedFullName',
        field: 'porProcessingStart.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('laboratory.grid.processingEndDate'),
        field: 'porProcessingEndDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110
    },
    {
        headerName: i18n.tc('laboratory.grid.processingEndPersonnel'),
        colId: 'processingEndPersonnel.normalizedFullName',
        field: 'porProcessingEnd.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('laboratory.grid.confectionDate'),
        field: 'porGenerationDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 110
    },
    {
        headerName: i18n.tc('laboratory.grid.confectionTech'),
        colId: 'generationPersonnel.fullName',
        field: 'porGeneration.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.portionType'),
        field: 'portionType.potName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.sampleType'),
        field: 'sample.sampleType.description',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('grid.detail.patName'),
        colId: 'sample.caseStudy.pathologist.normalizedFullName',
        field: 'sample.caseStudy.pathologist.fullName',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.dateLastRequestedAssay'),
        field: 'dateLastRequestedAssay',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 150
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.creationDate'),
        field: 'porRequestDate',
        type: AgGridColumnType.DATETIME,
        exportType: AgGridExportType.DATETIME,
        width: 150
    },
    {
        headerName: i18n.tc('grid.detail.priority'),
        colId: 'sample.caseStudy.priority.level',
        field: 'priority',
        type: AgGridColumnType.PRIORITY,
        exportField: 'priority.name',
        exportType: AgGridExportType.TEXT
    },
    {
        headerName: i18n.tc('laboratory.grid.columns.location'),
        field: 'sample.topographicCode.description',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.TEXT,
        width: 150
    },
    {
        headerName: i18n.tc('grid.hasAlarms'),
        headerTooltip: i18n.tc('grid.hasAlarms'),
        field: 'incidentsCount',
        type: AgGridColumnType.INCIDENT,
        exportType: AgGridExportType.INTEGER,
        filter: false,
        width: 55,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-exclamation-triangle')
        }
    },
    {
        headerName: i18n.tc('grid.attachments'),
        headerTooltip: i18n.tc('grid.attachments'),
        field: 'attachmentsCount',
        type: AgGridColumnType.ATTACHMENT_COUNT,
        exportType: AgGridExportType.INTEGER,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-paperclip')
        }
    },
    {
        headerName: i18n.tc('registry.btn.consultation'),
        headerTooltip: i18n.tc('registry.btn.consultation'),
        field: 'sample.caseStudy.caseOfReference',
        type: AgGridColumnType.TEXT,
        exportType: AgGridExportType.BOOLEAN,
        filter: false,
        hide: false,
        width: 55,
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-glasses')
        },
        cellRenderer: ({ value }: ICellRendererParams) => value === true ? '<i class="success far fa-check"/>' : ''
    }
]
